import { TextFieldWithCheckbox } from "@/controls/form-fields/CheckboxField";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Grid2 as Grid,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { LazyLoading } from "../../../../../controls/LazyLoading";
import {
	CATALOG_RESULT_SOURCE_INITIAL_VALUES,
	CatalogResultSource,
} from "../../../models/CatalogResultSource";
import {
	getCatalogResultSource,
	upsertCatalogResultSource,
} from "../../../repositories/CatalogResultSourceRepository";
import { useModalStyles } from "../../../styles/modalStyles";
type Props = {
	resultSourceId: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};
export const EditResultSource = ({ resultSourceId, show, onHide }: Props) => {
	const classes = useModalStyles();
	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const {
		register,
		control,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<CatalogResultSource>({
		defaultValues: CATALOG_RESULT_SOURCE_INITIAL_VALUES,
	});
	const handleClose = (
		event: Event | SyntheticEvent<any, Event>,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};
	const onSubmit: SubmitHandler<CatalogResultSource> = async (data) => {
		setIsSubmitLoading(true);
		const res = await upsertCatalogResultSource(data);
		if (res.ok) {
			const id = res.data.tables[0].rows[0].Id;
			onHide(true);
			setOpen(true);
		} else {
			setError(res.message);
		}
		setIsSubmitLoading(false);
	};
	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setIsLoading(true);
				const response = await getCatalogResultSource(resultSourceId);
				if (response.ok) {
					const resultSourceSelected = response.data.tables[0].rows[0];
					if (resultSourceSelected) {
						setValue("ResultSourceId", resultSourceSelected.ResultSourceId);
						setValue("ResultSourceName", resultSourceSelected.ResultSourceName);
						setValue("IsActive", resultSourceSelected.IsActive);
						setIsLoading(false);
					}
				} else {
					setError(response.message);
				}
			})();
		}
	}, [show]);
	return (
		<>
			<MesfModal
				open={show}
				handleClose={() => onHide(false)}
				maxWidth="sm"
				aria-labelledby="contained-modal-title-vcenter"
				title="EDIT RESULT SOURCE"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						className={classes.modalBody}
						style={{ padding: "15px 30px" }}
					>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "300px" }}
							>
								<LazyLoading />
							</Grid>
						)}
						{!isLoading && (
							<>
								<Grid container spacing={1}>
									<Grid size={{ xs: 12 }}>
										<TextField
											{...register("ResultSourceName", { required: true })}
											label="Name"
											variant="outlined"
											error={!!errors.ResultSourceName}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid>
									<Grid size={{ xs: 12 }}>
										<Controller
											name="IsActive"
											control={control}
											render={({ field }) => (
												<TextFieldWithCheckbox
													checkboxValue={field.value}
													onCheckboxChange={field.onChange}
													label="Enabled"
													className={classes.checkbox}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid>
							<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert
					elevation={6}
					variant="filled"
					severity="success"
					onClose={handleClose}
				>
					The result source was edited successfully
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
